// Team API Service
// Endpoints: /api/v1/teams

import axiosInstance from '@/app/api/axiosInstance';
import { Team, TeamCreate, TeamUpdate, TeamListResponse } from '@/app/types/team';
import { ListQueryParams } from '@/app/types/api';

const API_PATH = '/teams';

/** List teams in the organization. */
export const listTeams = async (
  params?: ListQueryParams & { include_inactive?: boolean }
): Promise<TeamListResponse> => {
  const response = await axiosInstance.get<TeamListResponse>(`${API_PATH}/`, { params });
  return response.data;
};

/** Get a single team with members. */
export const getTeam = async (id: number): Promise<Team> => {
  const response = await axiosInstance.get<Team>(`${API_PATH}/${id}`);
  return response.data;
};

/** Create a new team. */
export const createTeam = async (data: TeamCreate): Promise<Team> => {
  const response = await axiosInstance.post<Team>(`${API_PATH}/`, data);
  return response.data;
};

/** Update a team (pass member_user_ids to replace membership). */
export const updateTeam = async (id: number, data: TeamUpdate): Promise<Team> => {
  const response = await axiosInstance.patch<Team>(`${API_PATH}/${id}`, data);
  return response.data;
};

/** Delete a team (soft-delete by default; pass hard=true to remove permanently). */
export const deleteTeam = async (id: number, hard = false): Promise<void> => {
  await axiosInstance.delete(`${API_PATH}/${id}`, { params: { hard } });
};

/** Replace the full membership set of a team. */
export const setTeamMembers = async (id: number, userIds: number[]): Promise<Team> => {
  const response = await axiosInstance.patch<Team>(`${API_PATH}/${id}/members`, { user_ids: userIds });
  return response.data;
};

/** Add a single member to a team. */
export const addTeamMember = async (teamId: number, userId: number): Promise<Team> => {
  const response = await axiosInstance.post<Team>(`${API_PATH}/${teamId}/members/${userId}`);
  return response.data;
};

/** Remove a single member from a team. */
export const removeTeamMember = async (teamId: number, userId: number): Promise<Team> => {
  const response = await axiosInstance.delete<Team>(`${API_PATH}/${teamId}/members/${userId}`);
  return response.data;
};
